import React from "react";

/**
 * SiliconPay Africa silhouette — accurate vector traced from Natural Earth
 * (world-atlas 110m TopoJSON, simplified to ~0.4° tolerance).
 * Mainland + Madagascar are drawn, then overlaid with a network of gold dots
 * and connecting lines.
 */

// Accurate Africa + Madagascar SVG path (Natural Earth, simplified)
const AFRICA_PATH =
  "M61.7,268.4 L23.5,228.6 L22.7,218.1 L16.0,209.7 L26.0,191.7 L26.0,170.1 L20.2,163.3 L28.1,143.2 L49.2,114.3 L75.6,97.3 L77.5,77.9 L95.2,66.5 L102.4,54.3 L130.2,58.6 L157.1,48.0 L192.5,44.3 L227.8,44.4 L222.7,68.9 L258.9,80.1 L262.4,86.7 L287.3,94.9 L294.5,89.5 L295.1,80.3 L305.5,75.8 L359.9,90.4 L375.2,85.3 L382.5,89.9 L399.5,87.8 L404.4,100.5 L398.7,112.9 L385.9,97.9 L388.2,106.4 L410.0,141.7 L408.8,147.8 L418.7,156.0 L423.3,181.0 L430.1,185.5 L436.5,200.9 L466.4,227.0 L460.8,240.4 L466.2,248.0 L499.4,259.4 L507.9,248.7 L508.0,234.2 L524.0,229.7 L519.9,250.6 L505.4,279.1 L490.4,297.4 L443.8,337.6 L436.0,353.1 L433.0,366.4 L437.7,369.1 L435.9,381.3 L445.4,398.1 L447.6,427.1 L437.8,442.1 L403.3,464.8 L409.1,481.8 L408.3,496.8 L387.0,508.7 L384.3,531.1 L354.8,560.8 L336.8,569.4 L313.1,568.8 L294.6,575.7 L281.1,568.9 L280.9,552.6 L258.7,518.8 L251.6,482.0 L233.4,452.1 L233.3,435.3 L247.0,407.5 L247.4,397.9 L234.3,355.8 L211.3,326.8 L218.6,295.8 L215.8,291.0 L209.1,283.3 L189.8,287.1 L178.2,272.2 L160.0,273.2 L131.7,283.7 L111.9,280.4 L87.3,286.4 L61.7,268.4 Z " +
  "M517.3,436.8 L514.8,432.5 L494.3,502.9 L481.9,507.8 L471.7,503.2 L465.9,481.6 L474.2,466.9 L471.2,447.2 L474.8,438.4 L488.5,435.2 L498.8,426.4 L509.8,407.5 L516.2,418.7 L517.3,436.8 Z";

// Node positions (within the path's bounding box) — major African cities/hubs feel
const NODES = [
  [120, 95],   // Tunis / North coast
  [200, 75],   // Cairo
  [260, 110],  // Red Sea
  [320, 145],  // Mecca side
  [375, 180],  // Horn (Eritrea/Djibouti)
  [430, 220],  // Somalia
  [410, 270],  // Mogadishu
  [380, 310],  // Kenya
  [340, 360],  // Tanzania
  [310, 400],  // Zambia
  [285, 450],  // Zimbabwe
  [260, 500],  // Botswana
  [310, 540],  // Cape Town
  [70, 200],   // Mauritania
  [110, 240],  // Mali
  [160, 290],  // Burkina/Niger
  [185, 320],  // Nigeria — anchor
  [220, 290],  // Niger
  [230, 350],  // Cameroon
  [240, 420],  // Congo
  [260, 380],  // CAR
  [200, 380],  // Gabon
  [220, 460],  // Angola
  [505, 470],  // Madagascar
];

const LINKS = [
  [0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 8], [8, 9],
  [9, 10], [10, 11], [11, 12], [12, 22], [22, 11],
  [13, 14], [14, 15], [15, 16], [16, 17], [17, 18], [18, 19], [18, 20], [21, 19],
  [16, 2], [16, 7], [16, 8], [16, 22],
  [4, 7], [5, 8], [3, 1], [23, 8], [23, 10], [23, 12],
];

export default function AfricaMap({ className = "" }) {
  return (
    <svg
      viewBox="0 0 540 620"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="africaFill" cx="42%" cy="32%" r="78%">
          <stop offset="0%" stopColor="#2451d6" />
          <stop offset="55%" stopColor="#0e2a78" />
          <stop offset="100%" stopColor="#061544" />
        </radialGradient>
        <filter id="dotGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.6" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* Subtle dashed ring backdrop */}
      <circle cx="270" cy="310" r="290" fill="none" stroke="#f5b417" strokeWidth="0.6" opacity="0.16" strokeDasharray="3 6" />

      {/* Africa silhouette */}
      <path d={AFRICA_PATH} fill="url(#africaFill)" opacity="0.96" />
      <path d={AFRICA_PATH} fill="none" stroke="#1a3a9c" strokeWidth="0.6" opacity="0.5" />

      {/* Network connections */}
      <g stroke="#f5b417" strokeWidth="0.85" opacity="0.55">
        {LINKS.map(([a, b], i) => (
          <line
            key={i}
            x1={NODES[a][0]} y1={NODES[a][1]}
            x2={NODES[b][0]} y2={NODES[b][1]}
          />
        ))}
      </g>

      {/* Nodes */}
      <g filter="url(#dotGlow)">
        {NODES.map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r={i % 4 === 0 ? 3.6 : 2.3} fill="#f5b417" />
        ))}
        {/* Nigeria spotlight (siliconpay.com.ng) */}
        <circle cx={NODES[16][0]} cy={NODES[16][1]} r="6" fill="#f5b417" />
        <circle cx={NODES[16][0]} cy={NODES[16][1]} r="11" fill="none" stroke="#f5b417" strokeWidth="1" opacity="0.5" />
      </g>
    </svg>
  );
}
